import React, { useState, useEffect } from "react";
import axios from "axios";
import {Modal, Button, Form, Alert, Spinner, Badge} from "react-bootstrap";
import "./App.css";
import "./antifraud/antifraud.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Honeypot, useAntifraudTicket, withTicketRetry } from "./antifraud";

function App() {
  const APP_API = process.env.REACT_APP_API;
  const APP_TARGET = process.env.REACT_APP_TARGET;
  const PUBLIC_URL = process.env.PUBLIC_URL || '';
  const LEGAL_DOCUMENT_VERSION =
    process.env.REACT_APP_LEGAL_DOCUMENT_VERSION
    || (process.env.NODE_ENV === "test" ? "2026-08-20" : "");
  const LEGAL_ACCEPTANCE_ENABLED = Boolean(LEGAL_DOCUMENT_VERSION);

  const [showModal, setShowModal] = useState(true); // Открыто ли модальное окно
  const [step, setStep] = useState("login"); // Текущий шаг
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [personalData, setPersonalData] = useState(false);
  const [agreementPolicy, setAgreementPolicy] = useState(false);
  const [error, setError] = useState("");
  const [emailCodeRequired, setEmailCodeRequired] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [resendAfter, setResendAfter] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [showPassword, setShowPassword] = useState(false); // Показ/скрытие пароля
  const [productDisplay, setProductDisplay] = useState(null); // {name, type} или null
  const ticketApi = useAntifraudTicket({
    challengeUrl: APP_API ? `${APP_API}api/v1/antifraud/challenge/` : "",
    active: showModal,
  });
  const currentUrl = new URL(window.location.href || "https://ednice.ru/login/");
  const pathSegments = currentUrl.pathname.split("/").filter(Boolean);
  const normalizedPublicUrl = (PUBLIC_URL || "").replace(/\/+$/, "");
  const baseSegments = normalizedPublicUrl.split("/").filter(Boolean);
  let product_id = null;

  if (baseSegments.length > 0) {
    if (pathSegments.length > baseSegments.length) {
      const isSameBase = baseSegments.every((segment, index) => pathSegments[index] === segment);
      if (isSameBase) {
        product_id = pathSegments[baseSegments.length];
      }
    }
  } else if (pathSegments[0] === "login") {
    product_id = pathSegments[1] || null;
  } else {
    product_id = pathSegments[0] || null;
  }

  useEffect(() => {
    const runAutoLogin = async () => {
      if (!APP_API || !APP_TARGET) {
        setIsCheckingSession(false);
        return;
      }

      try {
        const payload = product_id ? { product: product_id } : {};
        const response = await axios.post(
          `${APP_API}api/v1/autologin/`,
          payload,
          { withCredentials: true }
        );
        if (response.data?.auth_code) {
          window.location.href = `${APP_TARGET}mountpoint/${response.data.auth_code}/`;
          return;
        }
      } catch (_error) {
        // Fallback to regular login form when no active session.
      }

      setIsCheckingSession(false);
    };

    runAutoLogin();
  }, [product_id, APP_API, APP_TARGET]);

  useEffect(() => {
    if (!product_id || !APP_API) return;
    axios.get(`${APP_API}api/v1/product-info/`, { params: { product: product_id } })
      .then((res) => {
        if (res.data?.name) {
          setProductDisplay({ name: res.data.name, type: res.data.type });
        }
      })
      .catch(() => {});
  }, [product_id, APP_API]);

  // Валидация email
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Обработка авторизации
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setError("Пожалуйста, введите правильный email адрес");
      return;
    }
    if (!password) {
      setError("Введите свой пароль");
      return;
    }
    if (!agreementPolicy) {
      setError("Вы должны ознакомиться с политикой обработки персональных данных");
      return;
    }
    setIsLoading(true);
    setError("");

    try {
      const response = await withTicketRetry(
        (antifraud) => axios.post(`${APP_API}api/v1/login/`, {
          email,
          password,
          product: product_id,
          antifraud,
        }),
        ticketApi
      );
      if (response.data.next_step !== "registration") {
        window.location.href = `${APP_TARGET}mountpoint/${response.data.auth_code}/`;
        // alert(`${APP_TARGET}mountpoint/${response.data.auth_code}/`);
      } else {
        setEmailCodeRequired(response.data.email_code_required === true);
        setStep("register");
      }

    } catch (err) {
      setError(err.response?.data?.detail || "Произошла ошибка. Мы уже разбираемся...");
    } finally {
      setIsLoading(false);
    }
  };

  const resendActive = resendAfter > 0;

  useEffect(() => {
    if (!resendActive) return undefined;
    const timer = window.setInterval(() => {
      setResendAfter((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendActive]);

  const authErrorText = (err) => {
    const payload = err.response?.data;
    if (payload?.code === "code_exhausted" || payload?.code === "code_expired") {
      return payload.detail || "Код больше не действует, запросите новый";
    }
    return payload?.detail || "Произошла ошибка. Мы уже разбираемся...";
  };

  const sendRegistrationCode = async () => {
    const response = await withTicketRetry(
      (antifraud) => axios.post(
        `${APP_API}api/v1/send_code/`,
        { email, locale: "ru", antifraud }
      ),
      ticketApi
    );
    if (response.data.next_step === "login") {
      setError("Этот email уже зарегистрирован. Войдите с паролем.");
      setStep("login");
      return false;
    }
    setResendAfter(response.data.resend_after_seconds || 180);
    setStep("code");
    return true;
  };

  // Обработка регистрации
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!fullName || !password) {
      setError("Пожалуйста, заполните все поля");
      return;
    }
    if (LEGAL_ACCEPTANCE_ENABLED && (!agreement || !personalData)) {
      setError("Вы должны принять пользовательское соглашение студента и ознакомиться с политикой обработки персональных данных");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      if (emailCodeRequired && step !== "code") {
        if (resendActive) {
          // Code already sent; back from «Изменить данные» without a new one.
          setStep("code");
          return;
        }
        await sendRegistrationCode();
        return;
      }
      const response = await withTicketRetry(
        (antifraud) => axios.post(
          `${APP_API}api/v1/register/`,
          {
            email,
            full_name: fullName,
            password: password,
            product: product_id,
            antifraud,
            code: emailCodeRequired ? verificationCode : undefined,
            agreements: LEGAL_ACCEPTANCE_ENABLED
              ? [
                  {
                    document_code: "student_agreement",
                    document_version: LEGAL_DOCUMENT_VERSION,
                    accepted: agreement
                  },
                  {
                    document_code: "personal_data",
                    document_version: LEGAL_DOCUMENT_VERSION,
                    accepted: personalData
                  }
                ]
              : []
          }
        ),
        ticketApi
      );
      ticketApi.markUsed();
      if (response.data.auth_code) {
        window.location.href = `${APP_TARGET}mountpoint/${response.data.auth_code}/`;
        // alert(`${APP_TARGET}mountpoint/${response.data.auth_code}/`);
      } else {
        setError("Произошла ошибка. Мы уже разбираемся...");
      }


    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendActive) return;
    setIsLoading(true);
    setError("");
    try {
      await sendRegistrationCode();
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="App d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="App">
      {/* Модальное окно */}
      <Modal show={showModal && !isCheckingSession} onHide={() => setShowModal(false)} backdrop="static" centered>
        <Modal.Header>
          <Modal.Title>Ednice авторизация</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Регистрация и авторизация</h5>
          {(productDisplay?.name || product_id) && (
            <h6>
              {productDisplay?.name
                ? (productDisplay.type === "course"
                  ? `Курс ${productDisplay.name}`
                  : productDisplay.type === "consultation"
                    ? `Консультация ${productDisplay.name}`
                    : `Вебинар ${productDisplay.name}`)
                : `Product: ${product_id}`}
            </h6>
          )}
              <div>
                {/* Шаг 1: Ввод email & password */}
                {step === "login" && (
                  <Form onSubmit={handleLoginSubmit}>
                    <Honeypot {...ticketApi.honeypotProps} />
                    {ticketApi.powSlot}
                    <Form.Group controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Введите свой email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="password" className="mt-3">
                      <Form.Label>Пароль</Form.Label>
                      <div className="password-input">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          aria-describedby="passwordHelpBlock"
                          placeholder="*"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          className="toggle-password"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                      <Form.Text id="passwordHelpBlock" muted>
                        Пароль должен быть не менее 6 символов и содержать только латинские буквы и цифры
                      </Form.Text>
                    </Form.Group>
                    <Form.Group controlId="agreement" className="mt-3">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            Я ознакомился с{" "}
                            <a href="https://ednice.ru/about/privacy-policy">
                              политикой обработки персональных данных
                            </a>
                          </>
                        }
                        checked={agreementPolicy}
                        onChange={(e) => setAgreementPolicy(e.target.checked)}
                      />
                    </Form.Group>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading || !agreementPolicy}
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Продолжить"
                      )}
                    </Button>
                  </Form>
                )}

                {/* Шаг 2: Регистрация */}
                {step === "register" && (
                  <Form onSubmit={handleRegisterSubmit}>
                    <Honeypot {...ticketApi.honeypotProps} />
                    {ticketApi.powSlot}
                    <Badge bg="info" as={"h6"}>Пользователь с таким email не найден: {email}</Badge>
                    <Alert variant="primary">
                      Пожалуйста, заполните регистрационную информацию о себе.
                    </Alert>
                    <Form.Group controlId="fullName">
                      <Form.Label>Ваше полное имя</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="*"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </Form.Group>
                    {LEGAL_ACCEPTANCE_ENABLED && (
                      <>
                        <Form.Group controlId="agreement" className="mt-3">
                          <Form.Check
                            type="checkbox"
                            label={
                              <>
                                Я согласен с условиями{" "}
                                <a href="https://ednice.ru/about/student-agreement/">
                                  пользовательского соглашения студента
                                </a>
                              </>
                            }
                            checked={agreement}
                            onChange={(e) => setAgreement(e.target.checked)}
                          />
                        </Form.Group>
                        <Form.Group controlId="personalData" className="mt-3">
                          <Form.Check
                            type="checkbox"
                            label={
                              <>
                                Я ознакомился с{" "}
                                <a href="https://ednice.ru/about/personal-data/">
                                  политикой обработки персональных данных
                                </a>
                              </>
                            }
                            checked={personalData}
                            onChange={(e) => setPersonalData(e.target.checked)}
                          />
                        </Form.Group>
                      </>
                    )}
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading || (LEGAL_ACCEPTANCE_ENABLED && (!agreement || !personalData))}
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        emailCodeRequired ? "Получить код" : "Регистрация"
                      )}
                    </Button>
                  </Form>
                )}

                {step === "code" && (
                  <Form onSubmit={handleRegisterSubmit}>
                    <Honeypot {...ticketApi.honeypotProps} />
                    {ticketApi.powSlot}
                    <Alert variant="primary">
                      Код из письма на {email}
                    </Alert>
                    <Form.Group controlId="verificationCode">
                      <Form.Label>Проверочный код</Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={12}
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading || !verificationCode}
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Регистрация"
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      className="w-100 mt-2"
                      disabled={isLoading || resendActive}
                      onClick={handleResendCode}
                    >
                      {resendActive ? `Повторно через ${resendAfter} с` : "Отправить повторно"}
                    </Button>
                    <Button
                      variant="link"
                      type="button"
                      className="w-100 mt-1"
                      onClick={() => {
                        setStep("register");
                        setError("");
                      }}
                    >
                      Изменить данные
                    </Button>
                  </Form>
                )}
              </div>

          {/* Отображение ошибок */}
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default App;